import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import type { Root } from '@amcharts/amcharts5';

type AmChartsCore = typeof import('@amcharts/amcharts5');
type AmChartsXy = typeof import('@amcharts/amcharts5/xy');
type AmChartsAnimated = typeof import('@amcharts/amcharts5/themes/Animated');

export type PgChartCreateChart = (
    root: Root,
    am5: AmChartsCore,
    am5xy: AmChartsXy,
) => void;

type AmChartsApi = {
    am5: AmChartsCore;
    am5xy: AmChartsXy;
    Animated: AmChartsAnimated['default'];
};

@Component({
    selector: 'app-pg-chart',
    templateUrl: './pg-chart.component.html',
    styleUrls: ['./pg-chart.component.scss'],
    standalone: false
})
export class PgChartComponent  implements OnInit, OnDestroy, AfterViewInit {

    @Input() createChart:PgChartCreateChart;

    private root: Root = null;

    constructor(private zone: NgZone) {}

    randomId:number = null;

    private static _loadPromise: Promise<AmChartsApi> = null;

    private static loadAmCharts(): Promise<AmChartsApi> {
        if(PgChartComponent._loadPromise == null) {
            PgChartComponent._loadPromise = Promise.all([
                import('@amcharts/amcharts5'),
                import('@amcharts/amcharts5/xy'),
                import('@amcharts/amcharts5/themes/Animated'),
            ]).then(([am5, am5xy, animated]) => ({
                am5,
                am5xy,
                Animated: animated.default,
            }));
        }

        return PgChartComponent._loadPromise;
    }

    private _destroyed = false;
  
    ngOnInit(): void {
        this.randomId = Math.random();
    }
  
    ngAfterViewInit() {
        PgChartComponent.loadAmCharts().then(({ am5, am5xy, Animated }) => {
            if(this._destroyed) return;

            this.zone.runOutsideAngular(() => {
                let root = am5.Root.new("pgchart_" + this.randomId);

                root.setThemes([Animated.new(root)]);
        
                this.createChart(root, am5, am5xy)
        
                this.root = root;
            });
        });
    }
  
    ngOnDestroy() {
        this._destroyed = true;

        this.zone.runOutsideAngular(() => {
            if (this.root) {
                this.root.dispose();
                this.root = null;
            }
        });
    }

    /** Returns a PNG data URI of the current chart, or null if not ready. */
    exportPng(): Promise<string | null> {
        return new Promise((resolve) => {
            const tryExport = (attempt: number) => {
                if (this._destroyed) {
                    resolve(null);
                    return;
                }
                if (this.root == null) {
                    if (attempt < 20) {
                        setTimeout(() => tryExport(attempt + 1), 100);
                    }
                    else {
                        resolve(null);
                    }
                    return;
                }

                this.zone.runOutsideAngular(() => {
                    import('@amcharts/amcharts5/plugins/exporting').then((exporting) => {
                        try {
                            const exp = exporting.Exporting.new(this.root, {
                                pngOptions: { quality: 1, maintainPixelRatio: true },
                            });
                            exp.export('png').then((dataUri) => {
                                exp.dispose();
                                resolve(dataUri || null);
                            }, () => {
                                exp.dispose();
                                resolve(null);
                            });
                        }
                        catch {
                            resolve(null);
                        }
                    }, () => resolve(null));
                });
            };

            tryExport(0);
        });
    }

}
